import type { ProjectPhase } from './types';

export const DRIVE_KINDS = [
  { value: 'ssd', label: 'SSD' }, { value: 'hdd', label: 'HDD' },
  { value: 'raid', label: 'RAID' }, { value: 'cloud', label: 'Cloud' },
] as const;
export const DRIVE_STATUSES = [
  { value: 'active', label: 'Active' }, { value: 'retired', label: 'Retired' },
  { value: 'lost', label: 'Lost' },
] as const;
export const PROJECT_DRIVE_ROLES = [
  { value: 'primary', label: 'Primary' }, { value: 'backup', label: 'Backup' },
  { value: 'archive', label: 'Archive' },
] as const;
export type DriveKind = typeof DRIVE_KINDS[number]['value'];
export type DriveStatus = typeof DRIVE_STATUSES[number]['value'];
export type ProjectDriveRole = typeof PROJECT_DRIVE_ROLES[number]['value'];
export interface DriveInput {
  label: string;
  kind: DriveKind;
  capacity_gb: number | null;
  free_gb?: number | null;
  location: string | null;
  serial: string | null;
  purchased_on: string | null;
  status: DriveStatus;
  notes: string | null;
}
export interface DriveRow extends DriveInput {
  id: string;
  sort_order: number;
  free_gb: number | null;
  free_checked_at: number | null;
  auto_backup: number;
  /** At most one live, active, manual drive is pinned on Drives → Active. */
  is_editing_drive: number;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}
export interface DriveListRow extends DriveRow { project_count: number }
export interface DrivePageRow extends DriveListRow {
  backup_count: number;
  without_backup_count: number;
}
export interface DrivePageProject extends DriveProject {
  client_name: string;
  phase: ProjectPhase;
  no_footage: number;
  backup_labels: string[];
  has_auto_backup: boolean;
}
export interface ProjectDriveInput { drive_id: string; role: ProjectDriveRole }
export interface ProjectDrive extends DriveRow, ProjectDriveInput {}
export interface DriveProject {
  project_id: string;
  name: string;
  project_number: number | null;
  role: ProjectDriveRole;
}
export interface DriveBoardProject extends DriveProject {
  phase: ProjectPhase;
  link_count: number;
  has_backup: boolean;
}
export interface DriveBoardRow extends Pick<DriveRow, 'id' | 'label' | 'kind' | 'capacity_gb' | 'free_gb' | 'free_checked_at' | 'auto_backup' | 'location' | 'updated_at' | 'status' | 'sort_order'> {
  projects: DriveBoardProject[];
}
export interface DriveBoard { drives: DriveBoardRow[]; unlinked_count: number }
export function driveCapacity(gb: number | null): string {
  return gb === null ? '—' : gb >= 1000 ? `${gb / 1000} TB` : `${gb} GB`;
}
// One primary and one backup keep the legacy text fields unambiguous.
// Any number of additional drives can use the archive role.
export function nextDriveRole(links: readonly (ProjectDriveInput & { auto_backup?: number })[]): ProjectDriveRole {
  const manual = links.filter(link => !link.auto_backup);
  return !manual.some((l) => l.role === 'primary') ? 'primary'
    : !manual.some((l) => l.role === 'backup') ? 'backup' : 'archive';
}

// Match migration 0056 exactly; other Unicode whitespace is part of a label.
export const DRIVE_LABEL_TRIM_CHARS = '\t\r\n ';
export function normalizeDriveLabel(label: string): string {
  let start = 0, end = label.length;
  while (start < end && DRIVE_LABEL_TRIM_CHARS.includes(label[start])) start++;
  while (end > start && DRIVE_LABEL_TRIM_CHARS.includes(label[end - 1])) end--;
  return label.slice(start, end);
}

// Legacy archive fields also hold status markers that are not drives. They
// stay as project text and never become register entries. Keep in step with
// migration 0056 and archiveMarkerSql in the worker.
export const ARCHIVE_STATUS_MARKERS = ['No Footage', 'Previous Project'] as const;
export const DELETED_MARKER_PREFIX = '[DELETED]';
export function isNoFootageMarker(label: string | null | undefined): boolean {
  return typeof label === 'string' && normalizeDriveLabel(label).toLowerCase() === 'no footage';
}

export function isArchiveMarker(label: string | null | undefined): boolean {
  if (!label) return false;
  const normalized = normalizeDriveLabel(label).toLowerCase();
  return ARCHIVE_STATUS_MARKERS.some((m) => m.toLowerCase() === normalized)
    || normalized.startsWith(DELETED_MARKER_PREFIX.toLowerCase());
}

export function projectDriveLabels(project: {
  drive_labels?: string[]; archive_drive_1: string | null; archive_drive_2: string | null;
}): string[] {
  return [...new Set([...(project.drive_labels ?? []), project.archive_drive_1, project.archive_drive_2]
    .filter((label): label is string => label !== null).map(normalizeDriveLabel).filter(Boolean))];
}


export interface UntaggedProjectRow {
  project_id: string;
  project_number: number | null;
  name: string;
  client_name: string;
  phase: ProjectPhase;
  primary: { drive_id: string; label: string } | null;
  backup: { drive_id: string; label: string } | null;
  has_auto_backup: boolean;
}
